//#region src/components/pd-spa-helper/controllers/ScrollController.ts
/**
* Reactive Controller for scroll threshold detection.
*
* Provides:
* - Threshold-based callbacks for scroll state changes
* - scrollToTop() utility for navigation
*
* @example
* ```typescript
* class MyComponent extends LitElement {
*   private scrollController = new ScrollController(this, {
*     threshold: 380, // Teaser height
*     onScrolledDown: () => this.teaserCollapsed = true,
*     onScrolledUp: () => this.teaserCollapsed = false,
*   });
*
*   render() {
*     return html`
*       <button @click=${() => this.scrollController.scrollToTop()}>
*         Back to top
*       </button>
*     `;
*   }
* }
* ```
*/
var ScrollController = class {
	constructor(host, options = {}) {
		this._handler = null;
		this._ticking = false;
		this._isPastThreshold = false;
		this._teaserCollapseEnabled = true;
		this._host = host;
		this._options = {
			threshold: options.threshold ?? 100,
			onScrolledDown: options.onScrolledDown ?? (() => {}),
			onScrolledUp: options.onScrolledUp ?? (() => {})
		};
		host.addController(this);
	}
	/**
	* Whether the scroll position is past the threshold
	*/
	get isPastThreshold() {
		return this._isPastThreshold;
	}
	/**
	* Scroll to the top of the page smoothly
	*/
	scrollToTop() {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: "smooth"
		});
	}
	/**
	* Update the threshold value dynamically
	*/
	setThreshold(threshold) {
		this._options.threshold = threshold;
	}
	/**
	* Enable/disable teaser collapse behavior.
	* When disabled, scroll threshold callbacks are not triggered.
	* Use this on pages without teaser to prevent scroll events from
	* incorrectly toggling teaserCollapsed state.
	*/
	setTeaserCollapseEnabled(enabled) {
		this._teaserCollapseEnabled = enabled;
	}
	hostConnected() {
		this._handler = this._createScrollHandler();
		document.addEventListener("scroll", this._handler, { passive: true });
		this._checkThreshold(window.scrollY);
	}
	hostDisconnected() {
		if (this._handler) {
			document.removeEventListener("scroll", this._handler);
			this._handler = null;
		}
	}
	/**
	* Create the scroll handler with requestAnimationFrame throttling
	*/
	_createScrollHandler() {
		return () => {
			if (!this._ticking) {
				window.requestAnimationFrame(() => {
					this._checkThreshold(window.scrollY);
					this._ticking = false;
				});
				this._ticking = true;
			}
		};
	}
	/**
	* Check if threshold is crossed and trigger callbacks
	*/
	_checkThreshold(scrollY) {
		if (!this._teaserCollapseEnabled) return;
		const wasPastThreshold = this._isPastThreshold;
		this._isPastThreshold = scrollY > this._options.threshold;
		if (this._isPastThreshold !== wasPastThreshold) {
			if (this._isPastThreshold) this._options.onScrolledDown();
			else this._options.onScrolledUp();
			this._host.requestUpdate();
		}
	}
};
//#endregion
export { ScrollController };
